import axiosServices from "../utils/axios";

import type {
  CreateBulkTaskPayload,
  CreateTaskPayload,
  QuickTask,
  Task,
  TaskStatus,
  TaskType,
  UpdateTaskPayload,
} from "../utils/types/tasks";

// API response types (snake_case from backend)
interface TaskResponse {
  task_id: string;
  title: string;
  description: string;
  agent_id: string;
  vessel_id: string;
  due_date: string | null;
  status: TaskStatus;
  created_dt: string;
  modified_dt: string;
  created_by: string;
  job_id?: string;
  completed_at?: string | null;
  comments?: string;
  type?: TaskType;
  job?: {
    job_id: string;
    status_id: string;
    current_status: string;
    voyage: string;
    assigned_agent_id?: string;
    status: {
      pick_list_id: string;
      value: string;
    };
    vessel: {
      vessel_id: string;
      name: string;
    };
    assigned_agent?: {
      person_id: string;
      name: string;
      email: string;
    };
    type?: TaskType;
  };
}

interface FetchTasksResponse {
  data: TaskResponse[];
  total: number;
}

// Transform backend response to frontend format
const transformTask = (taskResponse: TaskResponse): Task => ({
  id: taskResponse.task_id,
  title: taskResponse.title,
  description: taskResponse.description,
  agentId: taskResponse.agent_id,
  vesselId: taskResponse.vessel_id,
  dueDate: taskResponse.due_date,
  status: taskResponse.status,
  createdAt: taskResponse.created_dt,
  updatedAt: taskResponse.modified_dt,
  createdBy: taskResponse.created_by,
  jobId: taskResponse.job_id,
  completedAt: taskResponse.completed_at,
  comments: taskResponse.comments,
  type: taskResponse.type ?? "agent",
  job: taskResponse.job,
});

export const fetchTasks = async (params?: {
  search?: string;
  agentId?: string;
  vesselStatus?: string;
  jobId?: string;
  type?: string;
}): Promise<{ data: Task[]; total: number }> => {
  const queryParams: Record<string, any> = {};

  if (params?.search) {
    queryParams.q = params.search;
  }
  if (params?.agentId) {
    queryParams.agent_id = params.agentId;
  }
  if (params?.vesselStatus) {
    queryParams.vessel_status = params.vesselStatus;
  }
  if (params?.jobId) {
    queryParams.job_id = params.jobId;
  }
  if (params?.type) {
    queryParams.type = params.type;
  }

  const response = await axiosServices.get<FetchTasksResponse>("/tasks", {
    params: queryParams,
  });

  return {
    data: response.data.data.map(transformTask),
    total: response.data.total,
  };
};

export const createTask = async (task: CreateTaskPayload): Promise<Task> => {
  const response = await axiosServices.post<TaskResponse>("/tasks", {
    title: task.title,
    description: task.description,
    agent_id: task.agentId,
    due_date: task.dueDate,
    status: task.status,
    job_id: task.jobId,
    type: task.type,
    comments: task.comments,
  });
  return transformTask(response.data);
};

export const updateTask = async (task: UpdateTaskPayload): Promise<Task> => {
  const payload: Record<string, any> = {};

  if (task.title !== undefined) payload.title = task.title;
  if (task.description !== undefined) payload.description = task.description;
  if (task.agentId !== undefined) payload.agent_id = task.agentId;
  if (task.dueDate !== undefined) payload.due_date = task.dueDate;
  if (task.status !== undefined) payload.status = task.status;
  if (task.jobId !== undefined) payload.job_id = task.jobId;
  if (task.comments !== undefined) payload.comments = task.comments;
  if (task.type !== undefined) payload.type = task.type;

  const response = await axiosServices.put<TaskResponse>(
    `/tasks/${task.id}`,
    payload
  );
  return transformTask(response.data);
};

export const updateTaskStatus = async (
  taskId: string,
  status: TaskStatus
): Promise<Task> => {
  const payload: Record<string, any> = { status };

  // Set completed_at when moving to completed status
  if (status === "completed") {
    payload.completed_at = new Date().toISOString();
  }

  const response = await axiosServices.put<TaskResponse>(
    `/tasks/${taskId}`,
    payload
  );
  return transformTask(response.data);
};

export const deleteTask = async (taskId: string): Promise<void> => {
  await axiosServices.delete(`/tasks/${taskId}`);
};

export const fetchQuickTasks = async (): Promise<QuickTask[]> => {
  const response = await axiosServices.get("/quicktask");
  return response.data.data;
};

export const createBulkTask = async (
  task: CreateBulkTaskPayload
): Promise<{
  success: boolean;
  tasks: Task[];
}> => {
  const response = await axiosServices.post<{
    success: boolean;
    data:  TaskResponse[];
  }>("/tasks/bulk", {
    agent_id: task.agentId,
    due_date: task.dueDate,
    status: task.status,
    job_id: task.jobId,
    type: task.type,
  });
  const raw = response.data.data;
  const tasks = Array.isArray(raw)
    ? raw.map(transformTask)
    : [];
  return {
    success: response.data.success,
    tasks,
  };
};
